/**
 * Where a coach card sits inside the reply it illustrates — Spec K Task 5.
 *
 * Its own module rather than part of `MessageBubble`, because a component file
 * may only export components (`react-refresh/only-export-components`) — and
 * because `splitParagraphs` is half of a cross-language contract, which is
 * easier to keep in step when it is the first thing in a file named for it.
 */
import type { CoachArtifact } from "./artifacts/types";

/**
 * The reply's paragraphs, which is what a card's `after_paragraph` indexes.
 *
 * **This must match `paragraphs()` in `api/coach/card_selection.py` exactly.**
 * The backend resolves an anchor quote to an index against its own split; if
 * the two disagree about what paragraph 2 is, every card lands one off. Blank
 * lines only, empties dropped — kept this dumb so it stays easy to keep in
 * step, and neither side may change alone.
 */
export function splitParagraphs(content: string): string[] {
  return content
    .split(/\n\s*\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

interface Run {
  /** Consecutive paragraphs with no card between them, rendered as one block. */
  text: string;
  /** Cards anchored to this run's last paragraph. */
  cards: CoachArtifact[];
}

/**
 * Split the reply into runs of prose, each followed by the cards anchored to it.
 *
 * A run only ends where a card is actually anchored, so prose that has nothing
 * inserted into it stays one markdown block and keeps its own paragraph
 * margins. That matters: a run boundary falling inside a loose list would
 * render it as two lists, and this way that can only ever happen at the exact
 * point a card is being placed.
 *
 * Everything unanchored — no quote, an unresolvable one, an index past the end
 * of the reply — comes back as `trailing` and renders where cards rendered
 * before Spec K Task 5. A card is never dropped for lacking a position.
 */
export function buildRuns(
  content: string,
  artifacts?: CoachArtifact[],
): { runs: Run[]; trailing: CoachArtifact[] } {
  const paragraphs = splitParagraphs(content);
  if (!artifacts || artifacts.length === 0) {
    return { runs: [{ text: content, cards: [] }], trailing: [] };
  }

  const byParagraph = new Map<number, CoachArtifact[]>();
  const trailing: CoachArtifact[] = [];
  for (const artifact of artifacts) {
    const at = artifact.after_paragraph;
    if (at === null || at >= paragraphs.length) {
      trailing.push(artifact);
      continue;
    }
    const bucket = byParagraph.get(at);
    if (bucket) bucket.push(artifact);
    else byParagraph.set(at, [artifact]);
  }

  if (byParagraph.size === 0) {
    return { runs: [{ text: content, cards: [] }], trailing };
  }

  const runs: Run[] = [];
  let pending: string[] = [];
  paragraphs.forEach((paragraph, index) => {
    pending.push(paragraph);
    const cards = byParagraph.get(index);
    if (!cards) return;
    runs.push({ text: pending.join("\n\n"), cards });
    pending = [];
  });
  if (pending.length > 0) runs.push({ text: pending.join("\n\n"), cards: [] });

  return { runs, trailing };
}
